import {
  BriefcaseMedical,
  Check,
  ChevronDown,
  DollarSign,
  Stethoscope,
  User,
  Users,
} from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { CommonIcons } from 'common-src/assets/Icons';
import { CircleLoader } from 'common-src/components/base';
import { getDashboardClients } from 'common-src/models/Client';
import { getDashboardV2Appointments } from 'common-src/models/DashboardV2Appointment';
import { ColorsNew } from 'common-src/styles';

import useCustomSelector from 'src/hooks/useCustomSelector';

import {
  DEFAULT_OWNER_STATS,
  DEFAULT_STATS,
  pathwayLabel,
  TASK_QUEUE_OWNER_TABS,
  TASK_QUEUE_SORT_MODE,
  TASK_QUEUE_SORT_MODE_OPTIONS,
  TASK_QUEUE_STATUS_TABS,
  TASK_QUEUE_TEAM_TABS,
  TASK_STATUS_FILTER_KEYS,
} from '../../constants';
import {
  buildPatientGroups,
  buildPatientSearchMetadataById,
  buildPriorityGroups,
  buildReasonGroups,
  buildRenderableSectionItems,
  buildRenderableSectionTasks,
  buildTaskQueuePrioritySummary,
  buildTaskQueueSummaryLabel,
  buildTaskSearchOptions,
  getDelegatedSubtaskMeta,
  getLinkedTaskWaitingForTitle,
  getPatientSearchPathway,
  getTaskSearchPlaceholder,
  getTaskSearchSelectionLabel,
} from '../../helpers';
import { Badge } from '../Badge';
import PatientTaskSearch from '../PatientTaskSearch/PatientTaskSearch';
import TaskDetail from '../TaskDetail';
import TaskRow from '../TaskRow';
import styles from './TasksView.module.scss';

const TEAM_TAB_ICON_BY_KEY = {
  all: Users,
  member: User,
  ops: BriefcaseMedical,
  clinical: Stethoscope,
  rcm: DollarSign,
};

const TasksView = ({
  isLoading,
  ownerFilter,
  setOwnerFilter,
  ownerStats,
  activeTeam,
  setActiveTeam,
  statusFilter,
  setStatusFilter,
  patientSearch,
  setPatientSearch,
  stats,
  filtered,
  totalTaskCount,
  taskQueueTotals,
  searchableTasks,
  taskSortMode,
  setTaskSortMode,
  sortKey,
  sortDirection,
  onSort,
  isExpandingTaskSearch,
  onExpandTaskSearchScope,
  onSearchTaskPatients,
  onClearTaskSearch,
  hasMoreTasks,
  isLoadingMoreTasks,
  onLoadMoreTasks,
  selectedTask,
  setSelectedTask,
  expandedPatients,
  setExpandedPatients,
  onAssignToMe,
  assigningTaskId,
  onUnassignTask,
  unassigningTaskId,
  onMarkComplete,
  markingTaskId,
  onReassignTask,
  reassigningTaskId,
  onFollowTask,
  followingTaskId,
  onUnfollowTask,
  unfollowingTaskId,
  onBlockTask,
  blockingTaskId,
  onUnblockTask,
  unblockingTaskId,
  onEscalateTask,
  escalatingTaskId,
  onDeescalateTask,
  deescalatingTaskId,
  onDelegateSubtasks,
  delegatingSubtasksTaskId,
  onCancelSubtask,
  cancelingSubtaskId,
  currentUserId,
  currentUserName,
  onReplySent,
  delegatedSubtasksByParentId,
}) => {
  const safeStats = stats ?? DEFAULT_STATS;
  const safeOwnerStats = ownerStats ?? DEFAULT_OWNER_STATS;
  const safeFiltered = filtered ?? [];
  const safeTotalTaskCount = totalTaskCount ?? safeFiltered.length;
  const safeSearchableTasks = searchableTasks ?? [];
  const safeOwnerFilter = ownerFilter || 'all';
  const safeActiveTeam = activeTeam || 'all';
  const safeStatusFilter = statusFilter || 'open';
  const hasPatientSearch = !!patientSearch;
  const [expandedLinkedTasks, setExpandedLinkedTasks] = useState({});
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const sortMenuRef = useRef(null);
  const clients = useCustomSelector((state) => getDashboardClients(state)) || [];
  const appointments = useCustomSelector(getDashboardV2Appointments) || [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!sortMenuRef.current?.contains(event.target)) {
        setIsSortMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const appointmentsById = useMemo(
    () =>
      appointments.reduce((acc, appointment) => {
        const appointmentId = `${appointment?.id || ''}`.trim();
        if (!appointmentId) return acc;
        return { ...acc, [appointmentId]: appointment };
      }, {}),
    [appointments],
  );

  const patientSearchMetadataById = useMemo(
    () =>
      buildPatientSearchMetadataById({
        tasks: safeSearchableTasks,
        appointmentsById,
        clients,
      }),
    [appointmentsById, clients, safeSearchableTasks],
  );
  const taskSearchPlaceholder = useMemo(
    () => getTaskSearchPlaceholder(taskSortMode),
    [taskSortMode],
  );

  const taskSearchOptions = useMemo(
    () =>
      buildTaskSearchOptions({
        tasks: safeSearchableTasks,
        taskSortMode,
        patientSearchMetadataById,
      }),
    [patientSearchMetadataById, safeSearchableTasks, taskSortMode],
  );

  const patientGroups = useMemo(
    () =>
      buildPatientGroups({
        tasks: safeFiltered,
        appointmentsById,
        clients,
        preserveOrder: taskSortMode === TASK_QUEUE_SORT_MODE.PatientPriority,
      }),
    [appointmentsById, clients, safeFiltered, taskSortMode],
  );

  const reasonGroups = useMemo(() => buildReasonGroups(safeFiltered), [safeFiltered]);

  const priorityGroups = useMemo(() => buildPriorityGroups(safeFiltered), [safeFiltered]);

  const taskGroups = useMemo(() => {
    if (taskSortMode === TASK_QUEUE_SORT_MODE.ReasonPriority) return reasonGroups;
    if (taskSortMode === TASK_QUEUE_SORT_MODE.Priority) return priorityGroups;
    return patientGroups;
  }, [patientGroups, priorityGroups, reasonGroups, taskSortMode]);

  const prioritySummary = useMemo(() => {
    if (!hasPatientSearch && taskQueueTotals) {
      return {
        critical: taskQueueTotals.totalCritical ?? 0,
        high: taskQueueTotals.totalHigh ?? 0,
        medium: taskQueueTotals.totalMedium ?? 0,
        low: taskQueueTotals.totalLow ?? 0,
      };
    }

    return buildTaskQueuePrioritySummary(safeFiltered);
  }, [hasPatientSearch, safeFiltered, taskQueueTotals]);

  const priorityBucketCountsByKey = useMemo(() => {
    if (!hasPatientSearch && taskQueueTotals) {
      return {
        critical: taskQueueTotals.totalCritical ?? 0,
        high: taskQueueTotals.totalHigh ?? 0,
        medium: taskQueueTotals.totalMedium ?? taskQueueTotals.totalNormal ?? 0,
        low: taskQueueTotals.totalLow ?? 0,
      };
    }

    return null;
  }, [hasPatientSearch, taskQueueTotals]);

  const summaryLabel = useMemo(
    () => buildTaskQueueSummaryLabel(taskSortMode, taskGroups.length, safeTotalTaskCount),
    [safeTotalTaskCount, taskGroups.length, taskSortMode],
  );

  const togglePatient = (groupKey) => {
    setExpandedPatients((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const expandAllPatients = () => {
    setExpandedPatients(taskGroups.reduce((acc, group) => ({ ...acc, [group.key]: true }), {}));
  };

  const collapseAllPatients = () => {
    setExpandedPatients({});
  };

  const toggleLinkedTasks = (taskId) => {
    const normalizedTaskId = `${taskId || ''}`;
    if (!normalizedTaskId) return;

    setExpandedLinkedTasks((prev) => ({
      ...prev,
      [normalizedTaskId]: !prev[normalizedTaskId],
    }));
  };

  const sortIcon = (key) => {
    const isActive = sortKey === key;
    return (
      <img
        className={styles.sortIcon}
        style={{ transform: isActive && sortDirection === 'asc' ? 'rotate(-180deg)' : 'none' }}
        src={CommonIcons.sortDownIcon}
        alt={isActive ? `sort-${sortDirection}` : 'sort'}
      />
    );
  };

  const renderTaskRow = (group, task) => {
    const localDelegatedSubtasks =
      group.linkedTaskStructure.subtaskChildTasksByParentId.get(`${task.id}`) || [];
    const hydratedDelegatedSubtasks = delegatedSubtasksByParentId[`${task.id}`] || [];
    const mergedDelegatedSubtasks = Array.from(
      [...hydratedDelegatedSubtasks, ...localDelegatedSubtasks]
        .reduce((acc, subtask) => {
          const subtaskId = `${subtask?.id || ''}`.trim();
          if (!subtaskId) return acc;

          acc.set(subtaskId, subtask);
          return acc;
        }, new Map())
        .values(),
    );
    const delegatedSubtaskMeta = getDelegatedSubtaskMeta(mergedDelegatedSubtasks);

    return (
      <TaskRow
        key={task.id}
        task={task}
        isLinkedChild={group.linkedTaskStructure.linkedChildIds.has(`${task.id}`)}
        isSubtaskChild={group.linkedTaskStructure.subtaskChildIds.has(`${task.id}`)}
        isSubtask={!!task?.parentTaskId}
        parentTitle={
          task?.parentTaskId
            ? (group.tasks.find(
                (candidateTask) => `${candidateTask?.id || ''}` === `${task.parentTaskId || ''}`,
              )?.title ?? null)
            : null
        }
        linkedParentTitle={getLinkedTaskWaitingForTitle(task, group.tasks) ?? null}
        linkedChildCount={group.linkedTaskStructure.parentLinkedChildCount.get(`${task.id}`) ?? 0}
        subtaskChildCount={group.linkedTaskStructure.parentSubtaskChildCount.get(`${task.id}`) ?? 0}
        delegatedSubtaskMeta={delegatedSubtaskMeta}
        linkedExpanded={!!expandedLinkedTasks[`${task.id}`]}
        onToggleLinked={toggleLinkedTasks}
        selected={selectedTask?.id === task.id}
        onSelect={setSelectedTask}
        onAssignToMe={onAssignToMe}
        isAssigning={assigningTaskId?.has(task.id)}
      />
    );
  };

  const renderTasksTable = (group, items) => (
    <div className={styles.bucketRows}>
      <div className={styles.tableHeader}>
        <span />
        <span className={styles.headerCell}>Task</span>
        <span className={styles.headerCell} />
        <button
          type="button"
          onClick={() => onSort?.('status')}
          className={`${styles.headerCell} ${styles.sortButton} ${
            sortKey === 'status' ? styles.sortActive : ''
          }`}
        >
          Status
          {sortIcon('status')}
        </button>
        <button
          type="button"
          onClick={() => onSort?.('priority')}
          className={`${styles.headerCell} ${styles.sortButton} ${
            sortKey === 'priority' ? styles.sortActive : ''
          }`}
        >
          Priority
          {sortIcon('priority')}
        </button>
        <button
          type="button"
          onClick={() => onSort?.('due')}
          className={`${styles.headerCell} ${styles.sortButton} ${styles.headerDue} ${
            sortKey === 'due' ? styles.sortActive : ''
          }`}
        >
          Due
          {sortIcon('due')}
        </button>
      </div>
      {items.map((item) => {
        if (item.type === 'child-group') {
          return (
            <div key={item.key} className={styles.childGroupContainer}>
              {item.items.map((childItem) => {
                if (childItem.type === 'label') {
                  return (
                    <div key={childItem.key} className={styles.childSectionLabel}>
                      {childItem.label}
                    </div>
                  );
                }

                return renderTaskRow(group, childItem.task);
              })}
            </div>
          );
        }

        return renderTaskRow(group, item.task);
      })}
    </div>
  );

  const renderPatientGroup = (group) => {
    const isExpanded = !!expandedPatients[group.key];
    const pathway = pathwayLabel[group.pathway] ?? group.pathway;

    return (
      <div key={group.key} className={styles.patientGroup}>
        <button
          type="button"
          className={styles.patientRow}
          onClick={() => togglePatient(group.key)}
        >
          <ChevronDown
            size={12}
            strokeWidth={1.8}
            className={`${styles.patientChevron} ${!isExpanded ? styles.patientChevronCollapsed : ''}`}
          />
          <span className={styles.patientMarker} />
          <div className={styles.patientMain}>
            <div className={styles.patientTopRow}>
              <span className={styles.patientName}>{group.patientName}</span>
              {group.patientMrn ? (
                <span className={styles.patientMetaMuted}>{group.patientMrn}</span>
              ) : null}
            </div>
            <div className={styles.patientMetaRow}>
              {group.patientPhase != null ? (
                <span className={styles.patientMetaText}>Phase {group.patientPhase}</span>
              ) : null}
              {group.patientClinician ? (
                <span className={styles.patientMetaText}>{group.patientClinician}</span>
              ) : null}
              {pathway ? (
                <Badge variant="outline" className={styles.pathwayBadge}>
                  {pathway}
                </Badge>
              ) : null}
            </div>
          </div>
          <div className={styles.patientPills}>
            {group.criticalCount ? (
              <Badge variant="critical" className={styles.patientSummaryBadge}>
                {group.criticalCount} critical
              </Badge>
            ) : null}
            {group.blockedCount ? (
              <Badge variant="critical" className={styles.patientSummaryBadge}>
                {group.blockedCount} blocked
              </Badge>
            ) : null}
            {group.escalatedCount ? (
              <Badge variant="escalated" className={styles.patientSummaryBadge}>
                {group.escalatedCount} escalated
              </Badge>
            ) : null}
            {group.nowCount || group.laterCount ? (
              <span className={styles.summaryPillGroup}>
                {group.nowCount ? (
                  <span
                    className={`${styles.summaryPill} ${styles.summaryPillDark} ${
                      group.laterCount ? styles.summaryPillGrouped : ''
                    }`}
                  >
                    {group.nowCount} now
                  </span>
                ) : null}
                {group.laterCount ? (
                  <span
                    className={`${styles.summaryPill} ${
                      group.nowCount
                        ? `${styles.summaryPillGrouped} ${styles.summaryPillGroupedTrailing}`
                        : ''
                    }`}
                  >
                    {group.laterCount} later
                  </span>
                ) : null}
              </span>
            ) : null}
          </div>
          <span className={styles.patientDue}>{group.nextDueLabel}</span>
        </button>

        {isExpanded ? (
          <div className={styles.patientExpanded}>
            {[
              {
                key: 'priority',
                label: 'Priority',
                tasks: buildRenderableSectionTasks(
                  group.priorityTasks,
                  group.linkedTaskStructure,
                  expandedLinkedTasks,
                ),
                items: buildRenderableSectionItems(
                  group.priorityTasks,
                  group.linkedTaskStructure,
                  expandedLinkedTasks,
                  delegatedSubtasksByParentId,
                ),
              },
              {
                key: 'future',
                label: 'Future',
                tasks: buildRenderableSectionTasks(
                  group.futureTasks,
                  group.linkedTaskStructure,
                  expandedLinkedTasks,
                ),
                items: buildRenderableSectionItems(
                  group.futureTasks,
                  group.linkedTaskStructure,
                  expandedLinkedTasks,
                  delegatedSubtasksByParentId,
                ),
              },
            ].map((section, index) => {
              if (!section.tasks.length) return null;

              return (
                <div key={section.key} className={styles.bucketSection}>
                  <div className={styles.bucketHeader}>
                    <span
                      className={`${styles.bucketIndex} ${
                        section.key === 'priority' ? styles.bucketIndexDark : ''
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className={styles.bucketLabel}>{section.label}</span>
                    <span className={styles.bucketCount}>{section.tasks.length}</span>
                  </div>
                  {renderTasksTable(group, section.items)}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  const renderBucketGroup = (group) => {
    const isExpanded = !!expandedPatients[group.key];
    const renderableTasks = buildRenderableSectionTasks(
      group.tasks,
      group.linkedTaskStructure,
      expandedLinkedTasks,
    );
    const renderableItems = buildRenderableSectionItems(
      group.tasks,
      group.linkedTaskStructure,
      expandedLinkedTasks,
      delegatedSubtasksByParentId,
    );
    const isPriorityBucket = group.bucketType === 'priority';
    const priorityKey = group.key.replace('priority-', '');
    const displayCount =
      isPriorityBucket && priorityBucketCountsByKey
        ? (priorityBucketCountsByKey[priorityKey] ?? renderableTasks.length)
        : renderableTasks.length;

    return (
      <div key={group.key} className={styles.patientGroup}>
        <button
          type="button"
          className={styles.queueGroupRow}
          onClick={() => togglePatient(group.key)}
        >
          <ChevronDown
            size={12}
            strokeWidth={1.8}
            className={`${styles.patientChevron} ${!isExpanded ? styles.patientChevronCollapsed : ''}`}
          />
          <div className={styles.queueGroupMain}>
            <div className={styles.patientTopRow}>
              <Badge
                variant={isPriorityBucket ? group.badgeVariant || 'outline' : 'outline'}
                className={styles.patientSummaryBadge}
              >
                {group.title}
              </Badge>
              <span className={styles.patientMetaMuted}>{displayCount}</span>
            </div>
          </div>
        </button>

        {isExpanded ? (
          <div className={styles.patientExpanded}>
            <div className={styles.bucketSection}>
              <div className={styles.bucketHeader}>
                <span className={`${styles.bucketIndex} ${styles.bucketIndexDark}`}>1</span>
                <span className={styles.bucketLabel}>Tasks</span>
                <span className={styles.bucketCount}>{displayCount}</span>
              </div>
              {renderTasksTable(group, renderableItems)}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const ownerTabs = TASK_QUEUE_OWNER_TABS.map((tab) => ({
    ...tab,
    count: safeOwnerStats[tab.key] ?? 0,
  }));

  const teamTabs = TASK_QUEUE_TEAM_TABS.map((tab) => ({
    ...tab,
    count: tab.key === 'all' ? safeStats.total : (safeStats[tab.key] ?? 0),
    icon: TEAM_TAB_ICON_BY_KEY[tab.key],
  }));

  const statusTabs = TASK_QUEUE_STATUS_TABS.map((tab) => ({
    ...tab,
    label: tab.key === 'open' ? tab.label : `${tab.label} ${safeStats[tab.key] ?? 0}`,
  }));

  const activeSortModeLabel =
    TASK_QUEUE_SORT_MODE_OPTIONS.find((option) => option.value === taskSortMode)?.label ||
    'Member + Priority';

  return (
    <>
      <div className={styles.teamTabsBar}>
        <div className={styles.teamTabsGroup}>
          {teamTabs.map((team) => (
            <button
              key={team.key}
              type="button"
              onClick={() => setActiveTeam(team.key)}
              className={`${styles.teamTabButton} ${
                safeActiveTeam === team.key ? styles.teamTabActive : ''
              }`}
            >
              <span className={styles.teamTabIcon}>
                <team.icon size={14} strokeWidth={1.9} aria-hidden="true" />
              </span>
              {team.label}
              <span className={styles.teamTabCount}>{team.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            {ownerTabs.map((ownerTab) => (
              <button
                key={ownerTab.key}
                type="button"
                onClick={() => setOwnerFilter(ownerTab.key)}
                className={`${styles.filterButton} ${
                  safeOwnerFilter === ownerTab.key ? styles.filterActive : ''
                }`}
              >
                {ownerTab.label}
                <span className={styles.filterCount}>{ownerTab.count}</span>
              </button>
            ))}
          </div>
          <div className={styles.filterDivider} />
          <div className={styles.filterGroup}>
            {statusTabs.map((status) => (
              <button
                key={status.key}
                type="button"
                onClick={() => setStatusFilter(status.key)}
                className={`${styles.filterButton} ${
                  safeStatusFilter === status.key ? styles.filterActive : ''
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
          <div className={styles.filterActions}>
            <PatientTaskSearch
              patientSearch={patientSearch}
              setPatientSearch={setPatientSearch}
              getPatientSearchLabel={(selection) =>
                getTaskSearchSelectionLabel(selection, patientSearchMetadataById)
              }
              getPatientSearchPathway={getPatientSearchPathway}
              patientSearchMetadataById={patientSearchMetadataById}
              searchOptions={taskSearchOptions}
              searchPlaceholder={taskSearchPlaceholder}
              isSearchLoading={isExpandingTaskSearch}
              onExpandSearchScope={onExpandTaskSearchScope}
              onSearchPatients={
                taskSortMode === TASK_QUEUE_SORT_MODE.PatientPriority
                  ? onSearchTaskPatients
                  : undefined
              }
              onClearSearch={onClearTaskSearch}
            />
            <button
              type="button"
              onClick={expandAllPatients}
              className={styles.filterActionButton}
              disabled={!taskGroups.length}
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={collapseAllPatients}
              className={styles.filterActionButton}
              disabled={!taskGroups.length}
            >
              Collapse
            </button>
          </div>
        </div>
      </div>

      <div className={`${styles.layout} ${selectedTask ? styles.layoutWithDetail : ''}`}>
        <div className={styles.listCard}>
          <div className={styles.summaryBar}>
            <div className={styles.summaryMeta}>
              <span className={styles.summaryText}>{summaryLabel}</span>
              <div className={styles.prioritySummary}>
                <span className={`${styles.prioritySummaryItem} ${styles.prioritySummaryCritical}`}>
                  Critical: {prioritySummary.critical}
                </span>
                <span className={styles.prioritySummaryDivider}>·</span>
                <span className={`${styles.prioritySummaryItem} ${styles.prioritySummaryHigh}`}>
                  High: {prioritySummary.high}
                </span>
                <span className={styles.prioritySummaryDivider}>·</span>
                <span className={`${styles.prioritySummaryItem} ${styles.prioritySummaryMedium}`}>
                  Medium: {prioritySummary.medium}
                </span>
                <span className={styles.prioritySummaryDivider}>·</span>
                <span className={`${styles.prioritySummaryItem} ${styles.prioritySummaryLow}`}>
                  Low: {prioritySummary.low}
                </span>
              </div>
            </div>
            <div className={styles.summaryActions}>
              <div ref={sortMenuRef} className={styles.summarySortMenu}>
                <button
                  type="button"
                  className={styles.summarySortTrigger}
                  onClick={() => setIsSortMenuOpen((prev) => !prev)}
                >
                  Sorted by {activeSortModeLabel.toLowerCase()}
                  <ChevronDown
                    size={12}
                    strokeWidth={1.8}
                    className={`${styles.summarySortChevron} ${
                      isSortMenuOpen ? styles.summarySortChevronOpen : ''
                    }`}
                  />
                </button>
                {isSortMenuOpen ? (
                  <div className={styles.summarySortDropdown}>
                    {TASK_QUEUE_SORT_MODE_OPTIONS.map((option) => {
                      const isSelected = option.value === taskSortMode;

                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={styles.summarySortOption}
                          onClick={() => {
                            setTaskSortMode?.(option.value);
                            setIsSortMenuOpen(false);
                          }}
                        >
                          <Check
                            size={12}
                            strokeWidth={2}
                            className={`${styles.summarySortCheck} ${
                              isSelected ? styles.summarySortCheckVisible : ''
                            }`}
                          />
                          <span>{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {isLoading && (
            <div className={styles.loaderWrap}>
              <CircleLoader color={ColorsNew.darkGreen} strokeWidth={2} circleRadius={12} />
            </div>
          )}

          {!isLoading &&
            !taskGroups.length &&
            (hasPatientSearch ? (
              <div className={styles.emptyState}>No tasks match filters</div>
            ) : (
              <div className={styles.emptyState}>No tasks available.</div>
            ))}

          {!isLoading &&
            taskGroups.map((group) =>
              group.kind === 'patient' ? renderPatientGroup(group) : renderBucketGroup(group),
            )}
          {!isLoading && hasMoreTasks ? (
            <div className={styles.loadMoreWrap}>
              <button
                type="button"
                className={styles.loadMoreButton}
                onClick={() => onLoadMoreTasks?.()}
                disabled={isLoadingMoreTasks || !onLoadMoreTasks}
              >
                {isLoadingMoreTasks ? 'Loading more tasks...' : 'Load more tasks'}
              </button>
            </div>
          ) : null}
        </div>

        {selectedTask && (
          <div className={styles.detailPanel}>
            <TaskDetail
              task={selectedTask}
              cachedDelegatedSubtasks={
                selectedTask?.parentTaskId
                  ? undefined
                  : delegatedSubtasksByParentId[`${selectedTask.id}`] || []
              }
              onClose={() => setSelectedTask(null)}
              onAssignToMe={onAssignToMe}
              isAssigning={assigningTaskId?.has(selectedTask.id)}
              onUnassign={onUnassignTask}
              isUnassigning={unassigningTaskId === selectedTask.id}
              onMarkComplete={onMarkComplete}
              isMarking={markingTaskId === selectedTask.id}
              onReassign={onReassignTask}
              isReassigning={reassigningTaskId === selectedTask.id}
              onFollowTask={onFollowTask}
              isFollowing={followingTaskId === selectedTask.id}
              onUnfollowTask={onUnfollowTask}
              isUnfollowing={unfollowingTaskId === selectedTask.id}
              onBlockTask={onBlockTask}
              isBlocking={blockingTaskId === selectedTask.id}
              onUnblockTask={onUnblockTask}
              isUnblocking={unblockingTaskId === selectedTask.id}
              onEscalateTask={onEscalateTask}
              isEscalating={escalatingTaskId === selectedTask.id}
              onDeescalateTask={onDeescalateTask}
              isDeescalating={deescalatingTaskId === selectedTask.id}
              onDelegateSubtasks={onDelegateSubtasks}
              isDelegatingSubtasks={delegatingSubtasksTaskId === selectedTask.id}
              onCancelSubtask={onCancelSubtask}
              cancelingSubtaskId={cancelingSubtaskId}
              currentUserId={currentUserId}
              currentUserName={currentUserName}
              onReplySent={onReplySent}
              onSelectTask={setSelectedTask}
            />
          </div>
        )}
      </div>
    </>
  );
};

TasksView.propTypes = {
  isLoading: PropTypes.bool,
  ownerFilter: PropTypes.oneOf(['all', 'my', 'following', 'unassigned']),
  setOwnerFilter: PropTypes.func,
  ownerStats: PropTypes.shape({
    all: PropTypes.number,
    my: PropTypes.number,
    following: PropTypes.number,
    unassigned: PropTypes.number,
  }),
  activeTeam: PropTypes.oneOf(['all', 'member', 'ops', 'clinical', 'rcm']),
  setActiveTeam: PropTypes.func,
  statusFilter: PropTypes.oneOf(TASK_STATUS_FILTER_KEYS),
  setStatusFilter: PropTypes.func,
  patientSearch: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    preferredName: PropTypes.string,
  }),
  setPatientSearch: PropTypes.func,
  stats: PropTypes.shape({
    total: PropTypes.number,
    member: PropTypes.number,
    ops: PropTypes.number,
    clinical: PropTypes.number,
    rcm: PropTypes.number,
    cognitive: PropTypes.number,
    kisunla: PropTypes.number,
    'leqembi-iv': PropTypes.number,
    'leqembi-sc': PropTypes.number,
    critical: PropTypes.number,
    blocked: PropTypes.number,
    escalated: PropTypes.number,
    messages: PropTypes.number,
    assessments: PropTypes.number,
  }),
  filtered: PropTypes.arrayOf(PropTypes.object),
  totalTaskCount: PropTypes.number,
  taskQueueTotals: PropTypes.shape({
    totalCritical: PropTypes.number,
    totalHigh: PropTypes.number,
    totalMedium: PropTypes.number,
    totalNormal: PropTypes.number,
    totalLow: PropTypes.number,
  }),
  searchableTasks: PropTypes.arrayOf(PropTypes.object),
  taskSortMode: PropTypes.number,
  setTaskSortMode: PropTypes.func,
  sortKey: PropTypes.oneOf(['due', 'status', 'priority']),
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  onSort: PropTypes.func,
  isExpandingTaskSearch: PropTypes.bool,
  onExpandTaskSearchScope: PropTypes.func,
  onSearchTaskPatients: PropTypes.func,
  onClearTaskSearch: PropTypes.func,
  hasMoreTasks: PropTypes.bool,
  isLoadingMoreTasks: PropTypes.bool,
  onLoadMoreTasks: PropTypes.func,
  selectedTask: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parentTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  setSelectedTask: PropTypes.func,
  expandedPatients: PropTypes.objectOf(PropTypes.bool),
  setExpandedPatients: PropTypes.func,
  onAssignToMe: PropTypes.func,
  assigningTaskId: PropTypes.instanceOf(Set),
  onUnassignTask: PropTypes.func,
  unassigningTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onMarkComplete: PropTypes.func,
  markingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onReassignTask: PropTypes.func,
  reassigningTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onFollowTask: PropTypes.func,
  followingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUnfollowTask: PropTypes.func,
  unfollowingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBlockTask: PropTypes.func,
  blockingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUnblockTask: PropTypes.func,
  unblockingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onEscalateTask: PropTypes.func,
  escalatingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDeescalateTask: PropTypes.func,
  deescalatingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDelegateSubtasks: PropTypes.func,
  delegatingSubtasksTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCancelSubtask: PropTypes.func,
  cancelingSubtaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserName: PropTypes.string,
  onReplySent: PropTypes.func,
  delegatedSubtasksByParentId: PropTypes.object,
};

export default TasksView;
